// This file holds the authentication logic in one place.
// Any future auth logic changes should be made here.
import { useAuth } from "react-oidc-context";

import { PAGE_PERMISSIONS } from "../config/permissions";
import { AuthState as CognitoAuthState } from "../types/auth.types";
import { AuthState } from "../types/hooks.types";

// custom hook that encapsulates the authentication logic.
export const useAuthenticated = (): AuthState => {
  // access the auth status.
  const auth = useAuth();
  if (auth.isLoading) return { loading: true };
  if (auth.error) {
    console.log("Authentication error:", auth.error);
    return { error: auth.error };
  }
  return { isAuthenticated: auth.isAuthenticated };
};

// Check if the user is an Admin member (AdminManager or AdminMember from AWS Cognito user groups).
// The hook below extracts the cognito:groups from the user's ID token.
export const useIsAdmin = (): boolean => {
  const auth = useAuth();

  // If the user is not authenticated or still loading, return false.
  if (!auth.isAuthenticated || auth.isLoading || !auth.user) {
    return false;
  }

  // Extract AWS cognito user groups from the user's profile ID.
  // The cognito:groups is an array of group/s the user is part of.
  const userGroups = auth.user.profile["cognito:groups"] as string[] | undefined;
  console.log("user profile", auth.user.profile);

  // Check if the user is in either "AdminManager" or "AdminMember" groups.
  if (userGroups && Array.isArray(userGroups)) {
    return (
      userGroups.includes("AdminManager") ||
      userGroups.includes("AdminMember") ||
      userGroups.includes("GlobalAdmin")
    );
  }

  // If the user is not in admin groups, return false.
  return false;
};

export const useUserGroups = (): string[] => {
  const auth = useAuth();
  if (!auth.isAuthenticated || auth.isLoading || !auth.user) return [];
  return (auth.user.profile["cognito:groups"] as string[] | undefined) ?? [];
};

// Per the account-management API docs, an ID token carries the Cognito Username under the
// `cognito:username` claim (not the standard `email` claim, even though Username == email for
// every user in this pool) - this is what identifies "this is my own account" elsewhere in the app.
export const useCurrentUsername = (): string | null => {
  const auth = useAuth();
  if (!auth.isAuthenticated || auth.isLoading || !auth.user) return null;
  const cognitoUsername = auth.user.profile["cognito:username"] as string | undefined;
  return cognitoUsername ?? auth.user.profile.email ?? null;
};

// The signed-in user's display name, sourced from the given_name/family_name claims on the
// Cognito ID token (the same claims ProfileIcon and the Dashboard greeting read) - there's no
// separate name record in our own backend, it all comes from the token.
export const useCurrentUserFullName = (): string => {
  const auth = useAuth() as CognitoAuthState;
  const firstName = auth.user?.profile.given_name ?? "";
  const lastName = auth.user?.profile.family_name ?? "";
  return [firstName, lastName].filter(Boolean).join(" ");
};

export const useCanAccess = (page: string): boolean => {
  const groups = useUserGroups();
  if (groups.includes("GlobalAdmin")) return true;
  const allowed = PAGE_PERMISSIONS[page] ?? [];
  return groups.some((g) => allowed.includes(g));
};
